import { Link } from "react-router-dom";
import { ArrowLeft, Check, Cloud, CloudOff, Eye, History, Loader2, Maximize2, MousePointerClick, PanelLeft, PanelRight, Save, Share2, AlertTriangle, Undo2, Redo2, ScanEye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusSelect } from "@/components/common/StatusBadge";
import { DeviceToggle } from "./Canvas";
import { useEditor } from "./useEditorState";
import ProjectClient from "./ProjectClient";
import PublishButton from "./PublishButton";
import MobileMenu from "./MobileMenu";

const SAVE = {
  saved: { icon: Check, text: "Salvato", cls: "text-emerald-600" },
  dirty: { icon: Cloud, text: "Modifiche in corso", cls: "text-slate-500" },
  saving: { icon: Loader2, text: "Salvataggio…", cls: "text-sky-600", spin: true },
  error: { icon: CloudOff, text: "Errore di salvataggio", cls: "text-red-600" },
};

function Tip({ label, children }) {
  return <Tooltip><TooltipTrigger asChild>{children}</TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip>;
}

function Diagnostics() {
  const { diagnostics, setSelection, setUi } = useEditor();
  if (!diagnostics.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2.5 py-1" data-testid="diagnostics-badge">
          <AlertTriangle size={13} />{diagnostics.length}<span className="hidden sm:inline">{diagnostics.length === 1 ? "errore" : "errori"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <ul className="max-h-80 overflow-auto divide-y" data-testid="diagnostics-list">
          {diagnostics.map((d) => (
            <li key={d.id} className="p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">{d.name}</p>
                {d.kind !== "global" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelection({ kind: d.kind, id: d.id }); setUi({ rightTab: "code", rightOpen: true }); }}>Apri codice</Button>}
              </div>
              <pre className="mt-1.5 text-[11px] text-red-700 whitespace-pre-wrap font-mono max-h-28 overflow-auto">{d.message}</pre>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

const REVIEW_DOT = { rendering: "bg-amber-400 animate-pulse", analyzing: "bg-amber-400 animate-pulse", fixing: "bg-amber-400 animate-pulse", rendered: "bg-sky-500", analyzed: "bg-sky-500", ready: "bg-emerald-500" };

function ReviewButton() {
  const { ui, setUi } = useEditor();
  const dot = REVIEW_DOT[ui.reviewStatus];
  return (
    <Tip label="Revisione visiva: controllo qualità su screenshot reali">
      <Button variant="ghost" size="sm" className="relative h-9 px-2 shrink-0" onClick={() => setUi({ review: true })} data-testid="visual-review-btn">
        <ScanEye size={17} /><span className="hidden xl:inline ml-1.5 text-xs">Revisione</span>
        {dot && <span className={`absolute right-1 top-1.5 h-2 w-2 rounded-full ${dot}`} data-testid="visual-review-dot" />}
      </Button>
    </Tip>
  );
}

export default function Topbar({ onStatus, onSaveNow }) {
  const { project, saveState, ui, setUi, isDesktop, undo, redo, canUndo, canRedo } = useEditor();
  const s = SAVE[saveState];
  const ib = "h-10 w-10 sm:h-9 sm:w-9 shrink-0";
  return (
    <header className="h-14 shrink-0 bg-white border-b border-slate-200 flex items-center gap-1 sm:gap-2 px-1.5 sm:px-3" data-testid="editor-topbar">
      <Tip label="Torna ai progetti"><Button asChild variant="ghost" size="icon" className={ib}><Link to="/" data-testid="editor-back-btn" aria-label="Torna ai progetti"><ArrowLeft size={18} /></Link></Button></Tip>
      {!isDesktop && <Button variant="ghost" size="icon" className={ib} onClick={() => setUi({ leftOpen: true })} data-testid="open-left-panel-btn" aria-label="Pannello struttura"><PanelLeft size={18} /></Button>}
      <div className="min-w-0 flex-1 sm:flex-initial sm:min-w-[80px] sm:mr-2">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 truncate">{project.company_name || "Progetto"}</p>
        <p className="text-sm font-semibold text-slate-900 truncate sm:max-w-[180px] lg:max-w-[260px]" data-testid="editor-project-name">{project.name}</p>
      </div>
      <div className="hidden md:block"><ProjectClient pid={project.id} /></div>
      <div className="hidden lg:block"><StatusSelect status={project.status} onChange={onStatus} testId="editor-status-select" /></div>
      <div className="hidden min-[1440px]:flex items-center">
        <Tip label="Annulla (Ctrl+Z)"><Button variant="ghost" size="icon" className={ib} onClick={undo} disabled={!canUndo} data-testid="undo-btn" aria-label="Annulla"><Undo2 size={17} /></Button></Tip>
        <Tip label="Ripeti (Ctrl+Shift+Z o Ctrl+Y)"><Button variant="ghost" size="icon" className={ib} onClick={redo} disabled={!canRedo} data-testid="redo-btn" aria-label="Ripeti"><Redo2 size={17} /></Button></Tip>
      </div>
      <div className="hidden sm:flex flex-1 items-center justify-center gap-2">
        <DeviceToggle device={ui.device} onChange={(d) => setUi({ device: d })} />
        <Tip label="Seleziona sezioni nell'anteprima">
          <Button variant={ui.selectMode ? "default" : "ghost"} size="icon" className={`${ib} ${ui.selectMode ? "bg-sky-600 hover:bg-sky-700" : ""}`} onClick={() => setUi({ selectMode: !ui.selectMode })} data-testid="select-mode-btn"><MousePointerClick size={17} /></Button>
        </Tip>
        <Tip label="Anteprima a schermo intero"><Button variant="ghost" size="icon" className={ib} onClick={() => setUi({ fullscreen: true })} data-testid="fullscreen-btn"><Maximize2 size={16} /></Button></Tip>
      </div>
      <Diagnostics />
      <span className={`hidden min-[1440px]:inline-flex items-center gap-1.5 text-xs ${s.cls}`} data-testid="save-state"><s.icon size={14} className={s.spin ? "animate-spin" : ""} />{s.text}</span>
      <span className={`min-[1440px]:hidden inline-flex shrink-0 ${s.cls}`} title={s.text} aria-label={s.text} role="status" data-testid="save-state-compact"><s.icon size={15} className={s.spin ? "animate-spin" : ""} /></span>
      <div className="hidden min-[1440px]:flex items-center gap-2">
        <ReviewButton />
        <Tip label="Versioni"><Button variant="ghost" size="icon" className={ib} onClick={() => setUi({ versions: true })} data-testid="versions-btn"><History size={17} /></Button></Tip>
        <Tip label="Apri anteprima in una nuova scheda"><Button variant="ghost" size="icon" className={ib} onClick={() => { onSaveNow(); window.open(`/preview/${project.id}`, "_blank"); }} data-testid="open-preview-btn"><Eye size={17} /></Button></Tip>
        <Tip label="Link demo da condividere"><Button variant="ghost" size="sm" className="inline-flex px-3" onClick={() => setUi({ share: true })} data-testid="share-btn" aria-label="Condividi demo"><Share2 size={14} className="mr-1.5" />Demo</Button></Tip>
      </div>
      <MobileMenu onSaveNow={onSaveNow} />
      <Button size="sm" variant="outline" className="h-10 w-10 p-0 sm:h-9 lg:w-auto lg:px-3 shrink-0 border-navy-900 text-navy-900 sm:border-transparent sm:bg-navy-900 sm:text-white sm:hover:bg-navy-800" onClick={onSaveNow} disabled={saveState === "saving"} aria-label="Salva" data-testid="save-btn"><Save size={15} className="lg:mr-1.5" /><span className="hidden lg:inline">Salva</span></Button>
      <span className="sm:hidden mx-0.5 h-6 w-px shrink-0 bg-slate-200" aria-hidden="true" />
      <PublishButton />
      {!isDesktop && <Button variant="ghost" size="icon" className={ib} onClick={() => setUi({ rightOpen: true })} data-testid="open-right-panel-btn" aria-label="Pannello proprietà"><PanelRight size={18} /></Button>}
    </header>
  );
}
